import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Paper,
  Stack,
  SelectChangeEvent,
  Toolbar
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  EditNote as EditNoteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import { API_BASE, getAuthToken } from "../../api/agentApi";
import { useNavigate } from "react-router-dom";

// Types based on the OpenAPI schema
interface Tool {
  id: string;
  name: string;
  display_name: string;
  version: number; // Integer version (1, 2, 3, etc.)
  description: string;
  display_description: string;
  category: string | null;
  tags: string[];
  // code field removed - stored on disk
  // dependencies field removed
  parameters: Record<string, any>;
  returns: Record<string, any> | null;
  invocation_type: string;
  invocation_config: Record<string, any> | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_public: boolean;
  approved_by: string | null;
  approved_at: string | null;
  status: "draft" | "published" | "deprecated"; // 3 lifecycle statuses
  deprecation_reason: string | null;
  successor_version: number | null; // Integer version, not UUID
  usage_count: number;
}

// Category configuration
const CATEGORIES = [
  { value: "all", label: "All Categories", icon: "📦" },
  { value: "data_fetcher", label: "Data Fetchers", icon: "📥" },
  { value: "data_processor", label: "Data Processors", icon: "⚙️" },
  { value: "data_writer", label: "Data Writers", icon: "📤" },
  { value: "integration", label: "Integrations", icon: "🔗" },
  { value: "utility", label: "Utilities", icon: "🛠️" },
  { value: "ai_tool", label: "AI Tools", icon: "🤖" },
];

// Status configuration - Updated to match new 3-status schema
const STATUS_CONFIG = {
  draft: {
    label: "Draft",
    color: "default" as const,
    icon: <EditNoteIcon fontSize="small" />,
  },
  published: {
    label: "Published",
    color: "success" as const,
    icon: <CheckCircleIcon fontSize="small" />,
  },
  deprecated: {
    label: "Deprecated",
    color: "error" as const,
    icon: <WarningIcon fontSize="small" />,
  },
};

const ToolMarketplace: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [tools, setTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch tools from API
  const fetchTools = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      if (selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      if (selectedStatus !== "all") {
        params.append("status", selectedStatus);
      }
      if (visibilityFilter === "public") {
        params.append("is_public", "true");
      } else if (visibilityFilter === "private") {
        params.append("is_public", "false");
      }

      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/tools?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tools: ${response.statusText}`);
      }

      const data = await response.json();
      setTools(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedStatus, visibilityFilter]);

  // Filter tools by search query
  const filteredTools = tools.filter((tool: Tool) => {
    const query = searchQuery.toLowerCase();
    return (
      tool.name.toLowerCase().includes(query) ||
      tool?.display_description.toLowerCase().includes(query) ||
      tool?.tags.some((tag: string) => tag.toLowerCase().includes(query))
    );
  });

  // Group tools by category for stats
  const toolStats = {
    total: tools.length,
    byCategory: CATEGORIES.reduce((acc, cat) => {
      if (cat.value === "all") return acc;
      acc[cat.value] = tools.filter(
        (t: Tool) => t.category === cat.value
      ).length;
      return acc;
    }, {} as Record<string, number>),
    byStatus: {
      draft: tools.filter((t: Tool) => t.status === "draft").length,
      published: tools.filter((t: Tool) => t.status === "published").length,
      deprecated: tools.filter((t: Tool) => t.status === "deprecated").length,
    },
  };

  const handleViewTool = (toolId: string) => {
    navigate(`/tool/${toolId}`);
  };

  const getCategoryIcon = (category: string | null) => {
    const cat = CATEGORIES.find((c) => c.value === category);
    return cat ? cat.icon : "📦";
  };

  const getCategoryLabel = (category: string | null) => {
    const cat = CATEGORIES.find((c) => c.value === category);
    return cat ? cat.label : category || "Uncategorized";
  };
  const generateSummaryText = () => {
    return `Showing all ${filteredTools.length} ${
      filteredTools.length === 1 ? "Tool" : "Tools"
    }`;
  };

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Toolbar disableGutters sx={{ display: "flex", alignItems: "center"}}>
        <Typography variant="h4">
          Tool Catalog
        </Typography>
        </Toolbar>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Browse and discover reusable tools for your agents
        </Typography>
      

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          {/* Search */}
          <Box sx={{ flexGrow: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tools by name, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Category Filter */}
          <Box sx={{ minWidth: 200 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e: SelectChangeEvent) =>
                  setSelectedCategory(e.target.value)
                }
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{cat.icon}</span>
                      <span>{cat.label}</span>
                      {cat.value !== "all" && (
                        <Chip
                          label={toolStats.byCategory[cat.value] || 0}
                          size="small"
                          sx={{ ml: "auto" }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Status Filter */}
          <Box sx={{ minWidth: 150 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e: SelectChangeEvent) =>
                  setSelectedStatus(e.target.value)
                }
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="deprecated">Deprecated</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Visibility Filter */}
          <Box sx={{ minWidth: 150 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Visibility</InputLabel>
              <Select
                value={visibilityFilter}
                label="Visibility"
                onChange={(e: SelectChangeEvent) =>
                  setVisibilityFilter(e.target.value)
                }
              >
                <MenuItem value="all">All Tools</MenuItem>
                <MenuItem value="public">Public Only</MenuItem>
                <MenuItem value="private">Private Only</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Refresh Button */}
          <Tooltip title="Refresh tools">
            <IconButton onClick={fetchTools} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && filteredTools.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No tools found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search query
          </Typography>
        </Box>
      )}

      {/* Tools Grid */}
      {!isLoading && filteredTools.length > 0 && (
        <>
          {/* Display Results Summary with Stats */}
          <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {generateSummaryText()}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 5,
            }}
          >
            {filteredTools.map((tool: Tool) => (
              <Card
                key={tool.id}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  backgroundColor: '#ffffff',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: tool.status === 'published' 
                      ? 'linear-gradient(90deg, #4caf50, #66bb6a)'
                      : tool.status === 'deprecated'
                      ? 'linear-gradient(90deg, #f44336, #e57373)'
                      : 'linear-gradient(90deg, #9e9e9e, #bdbdbd)',
                    opacity: 0.8,
                  },
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3, pt: 2.5 }}>
                  {/* Header with Title and Status */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        color: 'text.primary',
                        letterSpacing: '-0.01em',
                        mb: 0.75,
                      }}
                    >
                      {tool.display_name}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      fontSize: '0.875rem',
                      lineHeight: 1.4,
                      minHeight: '2.5em',
                    }}
                  >
                    {tool?.display_description}
                  </Typography>

                  {/* Category Badge */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 1.5,
                  }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      {getCategoryIcon(tool.category)} {getCategoryLabel(tool.category)}
                    </Typography>
                  </Box>

                  {/* Tags */}
                  {tool.tags && tool.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                      {tool.tags.slice(0, 3).map((tag: string) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: '22px',
                            borderRadius: '6px',
                          }}
                        />
                      ))}
                      {tool.tags.length > 3 && (
                        <Chip
                          label={`+${tool.tags.length - 3} more`}
                          size="small"
                          variant="filled"
                          sx={{
                            fontSize: '0.7rem',
                            height: '22px',
                            bgcolor: 'action.selected',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Usage Count */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <GroupIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Used by <strong style={{ color: 'primary.main' }}>{tool.usage_count}</strong>{' '}
                      {tool.usage_count === 1 ? 'agent' : 'agents'}
                    </Typography>
                  </Box>
                </CardContent>

                <Divider />

                {/* Footer Actions */}
                <CardActions sx={{ 
                  px: 3, 
                  py: 2, 
                  justifyContent: 'space-between',
                  bgcolor: 'action.hover',
                }}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewTool(tool.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: 'primary.dark',
                      },
                    }}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
              
            ))}
          </Box>
        </>
      )}
    </Container>
  );
};

export default ToolMarketplace;
